import { useCallback, useMemo, useState } from 'react';

import { useLateFeesData } from './use-late-fees-data';
import { useLateFeesSettings } from './use-late-fees-settings';

export type ExclusionType = 'sessions' | 'classes' | 'events' | 'parents';

export function useExclusionModal(
  _type: ExclusionType | null,
  onClose: () => void,
) {
  const { exclusions, updateExclusions } = useLateFeesSettings();
  const { getItemsByType, getTitleByType } = useLateFeesData();

  const [isOpen, setIsOpen] = useState(false);
  const [activeType, setActiveType] = useState<ExclusionType | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItems, setSelectedItems] = useState<Array<string>>([]);

  const title = useMemo(() => {
    return activeType ? getTitleByType(activeType) : '';
  }, [activeType, getTitleByType]);

  const description = useMemo(() => {
    return `Select ${title.toLowerCase()} to exclude from late fee calculations`;
  }, [title]);

  const items = useMemo(() => {
    return activeType ? getItemsByType(activeType) : [];
  }, [activeType, getItemsByType]);

  const filteredItems = useMemo(() => {
    return items.filter((item) =>
      item.label.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [items, searchTerm]);

  const initializeModal = useCallback(
    (type: ExclusionType) => {
      setActiveType(type);
      setSelectedItems([...exclusions[type]]);
      setSearchTerm('');
      setIsOpen(true);
    },
    [exclusions],
  );

  const toggleItem = useCallback((value: string) => {
    setSelectedItems((prev) => {
      if (prev.includes(value)) {
        return prev.filter((item) => item !== value);
      } else {
        return [...prev, value];
      }
    });
  }, []);

  const selectAllFiltered = useCallback(() => {
    const filteredValues = filteredItems.map((item) => item.value);
    setSelectedItems((prev) => {
      const newSelection = new Set([...prev]);
      filteredValues.forEach((value) => newSelection.add(value));
      return Array.from(newSelection);
    });
  }, [filteredItems]);

  const deselectAllFiltered = useCallback(() => {
    const filteredValues = new Set(filteredItems.map((item) => item.value));
    setSelectedItems((prev) =>
      prev.filter((value) => !filteredValues.has(value)),
    );
  }, [filteredItems]);

  const saveSelections = useCallback(() => {
    if (activeType) {
      updateExclusions(activeType, selectedItems);
      setIsOpen(false);
      onClose();
    }
  }, [activeType, selectedItems, updateExclusions, onClose]);

  return {
    isOpen,
    title,
    description,
    searchTerm,
    setSearchTerm,
    filteredItems,
    selectedItems,
    toggleItem,
    selectAllFiltered,
    deselectAllFiltered,
    saveSelections,
    initializeModal,
  };
}
